import { Trans } from "@lingui/react/macro";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, RouteObject } from "react-router-dom";

import { Failed as FailedComponent } from "@/common/components/info/info";
import { FeatureMode } from "@/domain/plan/feature-mode.service";

import * as MarketplaceDetail from "./marketplace/marketplace-detail/marketplace.detail";
import { MarketplaceDetailBuy } from "./marketplace/marketplace-detail/marketplace.detail.buy";
import { MarketplaceDetailOrder } from "./marketplace/marketplace-detail/marketplace.detail.order";
import * as Marketplace from "./marketplace/marketplace-list/marketplace.list";
import { wrapWithPendingUserBanner } from "./routes.utils";
import * as PostalCodeRestoration from "./shared/components/postal-code-dialog/postal-code-restoration";
import * as Failed from "./shared/routes/failed/failed";
import { ContractLink, OrderLink } from "./shared/routes/success/order-link";
import * as Success from "./shared/routes/success/success";

const getMarketplaceDetailChildren = (): RouteObject[] => [
  {
    children: [
      {
        element: (
          <Failed.Failed
            title={<Trans>Nákup se nepodařilo uskutečnit</Trans>}
          />
        ),
        path: Failed.path,
      },
      {
        element: (
          <Success.Success
            action={<ContractLink />}
            content={
              <Success.Success.Content>
                <Trans>
                  <b>Transakce byla odeslána k potvrzení.</b> Jakmile bude
                  potvrzena, obratem vám pošleme upozornění a smlouvu k podpisu.
                </Trans>
              </Success.Success.Content>
            }
            title={
              <Success.Success.Title>
                <Trans>Odesláno k potvrzení</Trans>
              </Success.Success.Title>
            }
          />
        ),
        path: ":contractId/" + Success.path,
      },
    ],
    element: <MarketplaceDetailBuy />,
    path: "buy",
  },
  {
    children: [
      {
        element: (
          <Failed.Failed title={<Trans>Příkaz se nepodařilo nastavit</Trans>} />
        ),
        path: Failed.path,
      },
      {
        element: (
          <Success.Success
            action={<OrderLink />}
            content={
              <Success.Success.Content>
                <Trans>
                  <b>Váš příkaz byl úspěšně nastaven.</b> Jakmile bude
                  aktivován, budeme vás neprodleně informovat.
                </Trans>
              </Success.Success.Content>
            }
            title={
              <Success.Success.Title>
                <Trans>Příkaz úspěšně nastaven</Trans>
              </Success.Success.Title>
            }
          />
        ),
        path: `:orderId/offer/:offerId/${Success.path}`,
      },
    ],
    element: <MarketplaceDetailOrder />,
    path: "order",
  },
];

const getMarketplaceRoutes = (mode: FeatureMode): RouteObject[] => {
  const routes = [
    {
      children: [
        {
          children: [
            {
              children: getMarketplaceDetailChildren(),
              element: <MarketplaceDetail.MarketplaceDetail />,
              path: MarketplaceDetail.path,
            },
          ],
          element: (
            <ErrorBoundary
              fallbackRender={({ error }) => <FailedComponent error={error} />}
            >
              <PostalCodeRestoration.Restoration
                key={Marketplace.path}
                path={Marketplace.path}
              >
                {(params) => (
                  <PostalCodeRestoration.Params params={params}>
                    <Marketplace.Marketplace mode={mode} />
                  </PostalCodeRestoration.Params>
                )}
              </PostalCodeRestoration.Restoration>
            </ErrorBoundary>
          ),
          path: Marketplace.path,
        },
        {
          children: [
            {
              children: getMarketplaceDetailChildren(),
              element: <MarketplaceDetail.MarketplaceDetail />,
              path: MarketplaceDetail.path,
            },
          ],
          element: (
            <ErrorBoundary
              fallbackRender={({ error }) => <FailedComponent error={error} />}
            >
              <PostalCodeRestoration.Restoration
                key={Marketplace.path}
                path={Marketplace.path}
              >
                {(params) => (
                  <PostalCodeRestoration.Params params={params}>
                    <Marketplace.Marketplace mode={mode} />
                  </PostalCodeRestoration.Params>
                )}
              </PostalCodeRestoration.Restoration>
            </ErrorBoundary>
          ),
          path: Marketplace.path + "/:categorySlug",
        },
        {
          children: [
            {
              children: getMarketplaceDetailChildren(),
              element: <MarketplaceDetail.MarketplaceDetail />,
              path: MarketplaceDetail.path,
            },
          ],
          element: (
            <ErrorBoundary
              fallbackRender={({ error }) => <FailedComponent error={error} />}
            >
              <PostalCodeRestoration.Restoration
                key={Marketplace.path}
                path={Marketplace.path}
              >
                {(params) => (
                  <PostalCodeRestoration.Params params={params}>
                    <Marketplace.Marketplace mode={mode} />
                  </PostalCodeRestoration.Params>
                )}
              </PostalCodeRestoration.Restoration>
            </ErrorBoundary>
          ),
          path: Marketplace.path + "/:categorySlug/:subcategorySlug",
        },
      ],
      element: <Outlet />,
    },
  ];

  if (mode === "full") {
    return wrapWithPendingUserBanner(routes);
  }

  return routes;
};

export { getMarketplaceRoutes };
